import { qualifyingCreatureNextToTarget } from '../CombatManager';
import { prepareCreatureWeaponAttack } from '../../../../shared/rules/creatureWeaponAttack';
import { resolveSavingThrowModifier, hasConditionImmunity } from './RogueCombatFeatures';
import { creatureMechanicsProjection, assertCreatureCombatReadiness } from './CreatureAbilityCatalog';
import {
  CreatureMechanicsError, creatureActionPrerequisite, creatureCheckAdvantage,
  resolveCreatureZeroHp, creatureZeroHpSavePlan, validateCreatureMechanics
} from '../../../../shared/rules/creatureAbilities';

/** SRD 5.1 (2014) monster abilities; date_of_change: 2026-09-05.
 * These functions build staged results. They never mutate a combatant, spend
 * an action, or look up a template during combat.
 */
const view = actor => ({ ...creatureMechanicsProjection(actor), ...actor });
const turnKey = state => {
  const round = state.round ?? state.currentRound;
  const turn = state.turn ?? state.currentTurn;
  if (!Number.isInteger(round) || round < 1 || !Number.isInteger(turn) || turn < 0) throw new CreatureMechanicsError('combatState', '', 'round/turn', 'an explicit positive round and nonnegative turn index');
  return `${round}:${turn}`;
};

export const validateCreatureActionRequest = (request, state) => {
  const { attacker, actionData, targets = [] } = request;
  assertCreatureCombatReadiness(attacker, actionData);
  targets.forEach(target => assertCreatureCombatReadiness(target));
  if (!actionData?.mechanics) return;
  const actor = view(attacker);
  const embedded = [...(actor.actions || []), ...(actor.bonusActions || []), ...(actor.reactions || [])].find(entry => entry.id === actionData.id);
  if (!embedded || ['mechanics', 'attack', 'activationType', 'executionRoute', 'standardActionKey'].some(key => JSON.stringify(embedded[key]) !== JSON.stringify(actionData[key]))) throw new CreatureMechanicsError(attacker.id, actionData.id, 'actionData', 'the embedded action definition; requests may not rewrite its mechanics');
  const reason = creatureActionPrerequisite(actor, actionData);
  if (reason) throw new CreatureMechanicsError(attacker.id, actionData.id, 'equipmentState', reason);
  if (actionData.multiattackParentId) {
    const sequence = attacker.combatFlags?.multiattack;
    if (!sequence || sequence.parentId !== actionData.multiattackParentId || sequence.nextIndex !== actionData.multiattackIndex
      || sequence.actionIds[sequence.nextIndex] !== actionData.id || sequence.turnKey !== turnKey(state)) {
      throw new CreatureMechanicsError(attacker.id, actionData.id, 'combatFlags.multiattack', 'the active parent sequence and next legal component on this turn');
    }
    request.__actionEconomyConsumed = true;
  } else if (request.__actionEconomyConsumed || actionData.__actionEconomyConsumed) {
    throw new CreatureMechanicsError(attacker.id, actionData.id, '__actionEconomyConsumed', 'a validated compound-action component; a client flag cannot bypass the action economy');
  }
  for (const target of targets) {
    for (const rider of actionData.mechanics.onHit || []) {
      const save = resolveSavingThrowModifier(view(target), rider.ability);
      if (!save.valid) throw new CreatureMechanicsError(target.id, actionData.id, `savingThrows.${rider.ability}`, save.error);
    }
  }
};

export const expandCreatureMultiattack = (action, actions, actorId) => {
  validateCreatureMechanics(action, actorId);
  const definitions = new Map(actions.map(entry => [entry.id, entry]));
  return action.mechanics.components.flatMap(component => {
    const definition = definitions.get(component.actionId);
    if (!definition || definition.mechanics?.kind !== 'attack') throw new CreatureMechanicsError(actorId, action.id, 'mechanics.components.actionId', `embedded attack ${component.actionId}`);
    return Array.from({ length: component.count }, () => ({ ...definition, isAttack: true, isAbility: false, targetingRequired: true }));
  }).map((entry, index, entries) => ({ ...entry, multiattackParentId: action.id, multiattackIndex: index, componentIndex: index + 1, componentTotal: entries.length }));
};

export const beginCreatureMultiattack = (attacker, action, state) => {
  const actor = view(attacker);
  const components = expandCreatureMultiattack(action, actor.actions || [], attacker.id);
  return {
    success: true, actionType: 'action', actionName: action.name, results: [],
    effect: `${action.name}: ${components.length} attacks, one action.`,
    combatStageUpdates: { attacker: { combatFlags: { multiattack: {
      parentId: action.id, actionIds: components.map(entry => entry.id), nextIndex: 0, turnKey: turnKey(state)
    } } } }
  };
};

export const prepareCreatureAttack = (attacker, target, action, state) => {
  const actor = view(attacker);
  return prepareCreatureWeaponAttack(actor, target, action, state, distanceFeet =>
    Boolean(qualifyingCreatureNextToTarget(actor, target, state, { requireAttackerAlly: true, distanceFeet })));
};

export const resolveCreatureAttackRiders = async (attacker, target, action, result, state, resolveSave) => {
  if (!result.attackRoll?.isHit) return null;
  result.creatureEffects = action.creatureEffects || [];
  const actor = view(target);
  let stage = null;
  for (const rider of action.mechanics?.onHit || []) {
    const immune = hasConditionImmunity(actor, rider.condition);
    if (immune) {
      result.creatureEffects.push({ kind: 'saveCondition', condition: rider.condition, immune: true });
      continue;
    }
    const save = await resolveSave(actor, rider.ability, rider.dc, state);
    if (!save.success) throw new CreatureMechanicsError(target.id, action.id, 'savingThrow', save.cancelled ? 'a completed saving throw; the action was cancelled without committing' : save.error);
    result.creatureEffects.push({ kind: 'saveCondition', condition: rider.condition, savingThrow: save.savingThrow, applied: !save.saveSuccess });
    if (!save.saveSuccess) {
      stage = { ...(stage || {}), id: target.id, conditionsToAdd: [...(stage?.conditionsToAdd || []), rider.condition], statusEffects: { ...(stage?.statusEffects || {}), [rider.condition]: true } };
    }
  }
  // The server repeats the zero-HP predicate on the authoritative damage
  // packet; local rooms commit this same staged HP result.
  if (actor.traits?.some(trait => trait.mechanics?.kind === 'surviveAtZero') && result.updatedTarget) {
    const hpAfter = result.updatedTarget.hpObj?.current ?? result.updatedTarget.hp;
    const damageTypes = result.damage?.details?.map(detail => detail.damageType || detail.type)
      || action.attack?.damage?.map(d => d.type) || [];
    const packet = {
      hpBefore: actor.hpObj?.current ?? actor.hp,
      hpAfter,
      damage: result.damage?.total || 0,
      damageTypes,
      critical: result.attackRoll.isCritical === true
    };
    const faces = new Map();
    for (const planned of creatureZeroHpSavePlan(actor, packet)) {
      if (planned.excluded) continue;
      const save = await resolveSave(actor, planned.ability, planned.dc, state);
      if (!save.success) throw Object.assign(new Error(save.error || 'Triggered save cancelled; nothing committed.'), { code: save.cancelled ? 'ACTION_CANCELLED' : 'CREATURE_SAVE_INVALID', cancelled: save.cancelled === true });
      faces.set(planned.abilityId, save.savingThrow.d20Roll);
      if (save.saveSuccess) break;
    }
    const resolution = resolveCreatureZeroHp(actor, packet, (_sides, abilityId) => faces.get(abilityId));
    result.creatureEffects.push(...resolution.saves);
    result.updatedTarget = {
      ...result.updatedTarget, hp: resolution.hpAfter,
      ...(result.updatedTarget.hpObj ? { hpObj: { ...result.updatedTarget.hpObj, current: resolution.hpAfter } } : {})
    };
    result.creatureSaves = resolution.saves;
  }
  return stage;
};

export const mergeCreatureAttackStages = (attacker, action, state, results, initial) => {
  let stage = initial;
  const usage = { ...(attacker.combatFlags?.creatureAbilityUses || {}) };
  let changed = false;
  results.filter(result => result.attackRoll?.isHit).forEach(result => {
    (result.creatureEffects || []).filter(effect => effect.usage).forEach(effect => { usage[effect.abilityId] = turnKey(state); changed = true; });
  });
  if (changed) stage = { ...(stage || {}), combatFlags: { ...(stage?.combatFlags || {}), creatureAbilityUses: usage } };
  if (action.multiattackParentId) {
    const sequence = attacker.combatFlags.multiattack;
    const nextIndex = sequence.nextIndex + 1;
    stage = { ...(stage || {}), combatFlags: { ...(stage?.combatFlags || {}), multiattack: nextIndex < sequence.actionIds.length ? { ...sequence, nextIndex } : null } };
  }
  return stage;
};

export const creatureCheckModifiers = (attacker, check) => (
  creatureCheckAdvantage(view(attacker), check).length ? { advantage: true } : {}
);

// Keep stable canonical ability IDs available to the action audit without
// asking presentation code to rediscover the rules predicate.
// rule_reference: SRD 5.1, advantage on explicitly scoped ability checks.
// date_of_change: 2026-09-06
export const creatureCheckAdvantageSources = (attacker, check) => (
  creatureCheckAdvantage(view(attacker), check)
);
